import { Subscription, concatMap } from 'rxjs';
import { io } from 'socket.io-client';
import { fromCallback } from 'xstate';

import { cacheUtils } from '@eluve/apollo-client';
import { appointmentSegmentFragment } from '@eluve/frontend-appointment-hooks';
import { CorrelatedLogger } from '@eluve/logger';
import {
  AUDIO_PACKET,
  AudioPacket,
  CONNECT,
  CONNECT_ERROR,
  DISCONNECT,
  INITIALIZE_AUDIO_HANDLER,
  InitializePacketHandler,
  STOP_RECORDING,
  TRANSCRIPT_EVENT,
  TranscriptEvent,
} from '@eluve/socket-messages';
import { SupportedAudioFormat } from '@eluve/user-local-files';

import {
  AudioPacketSubject,
  AudioPacket as TranscriptionAudioPacket,
} from '../types';

export type WebSocketEvents =
  | {
      type: 'connection.connect';
      authToken: string;
    }
  | {
      type: 'connection.connected';
    }
  | {
      type: 'connection.failed';
      message: string;
    }
  | {
      // when the connection is disconnected
      type: 'connection.disconnected';
    }
  | {
      type: 'connection.disconnected.unexpectedly';
      message: string;
    }
  | {
      type: 'connection.sendAudioPacket.failed';
      message: string;
    }
  | {
      type: 'connection.transcript.received';
      transcript: TranscriptEvent;
    };

type Input = {
  format: SupportedAudioFormat;
  tenantId: string;
  appointmentId: string;
  segmentId: string;
  audioPacketSubject: AudioPacketSubject;
  recordingStartedAt: string;
  numAudioPacketsConsumedByWebSocket: number;
  totalBytesConsumedByWebSocket: number;
  logger: CorrelatedLogger;
  apiDomain: string;
};

const MAX_CONSECUTIVE_FAILED_ATTEMPTS = 3;

// Backstop if the teardown drain never completes (machine destroyed, wedged
// ACK) so the socket can't leak. Generous so it never fires mid-drain. [ENG-4721]
const TEARDOWN_SAFETY_TIMEOUT_MS = 30_000;

export const websocket = fromCallback<WebSocketEvents, Input>(
  ({ input, sendBack, receive }) => {
    const { format, appointmentId, tenantId, logger } = input;

    let packetNumber = 0;
    let audioPacketSubscription: Subscription | undefined;
    let consecutiveFailedAttemptCount = 0;
    let isClientInitiatedDisconnect = false;
    let hasStoppedRecording = false;
    let teardownSafetyTimer: ReturnType<typeof setTimeout> | undefined;

    // Set once any packet goes un-ACKed under the disconnect threshold. We can't
    // safely resend (server appends in receive order, no packetNumber dedup), so
    // we mark the file as possibly-truncated and let the drain fall back to the
    // backup instead of finalizing as a clean stop. [ENG-4721]
    let hadUnackedAudioPacket = false;

    // Tell the server to finalize the GCS file, only after the drain has sent and
    // ACKed every packet incl. the encoder tail — emitting earlier finalizes a
    // truncated file (the bug this fixes). No disconnect() here: that can drop the
    // buffered STOP_RECORDING before it flushes; the server closes the socket once
    // finalized, with the safety timer as backstop. [ENG-4721]
    const finalizeServerRecording = () => {
      if (hasStoppedRecording) return;
      hasStoppedRecording = true;
      isClientInitiatedDisconnect = true;
      if (socket.connected) {
        socket.emit(STOP_RECORDING);
      }
    };

    // Hard-close everything. Only used as a backstop (see the teardown safety
    // timer) — the normal stop path lets the drain finish and the server close
    // the socket.
    const hardClose = () => {
      audioPacketSubscription?.unsubscribe();
      if (socket.connected) {
        isClientInitiatedDisconnect = true;
        socket.disconnect();
      }
      socket.close();
    };

    const sendAudioPacket = async (packet: TranscriptionAudioPacket) => {
      if (!socket.connected || isClientInitiatedDisconnect) {
        sendBack({
          type: 'connection.unavailable',
        });
        return;
      }

      let isResponseOk = true;
      const currentPacketNumber = packetNumber++;
      try {
        const audioPacket: AudioPacket = {
          data: packet.data,
          timestamp: packet.timestamp,
          packetNumber: currentPacketNumber,
        };
        const response = await socket
          .timeout(3000)
          .emitWithAck(AUDIO_PACKET, audioPacket);

        if (!response.ok) {
          isResponseOk = false;
          throw new Error('Failed to send audio packet to server');
        }
        consecutiveFailedAttemptCount = 0;
        input.numAudioPacketsConsumedByWebSocket =
          (input.numAudioPacketsConsumedByWebSocket ?? 0) + 1;
        input.totalBytesConsumedByWebSocket =
          (input.totalBytesConsumedByWebSocket ?? 0) + packet.data.size;
      } catch (error) {
        if (!socket.connected || isClientInitiatedDisconnect) {
          // If the connection is closed or the client initiated the disconnect due to an error,
          // we don't need to handle the error here since we already logged this condition elsewhere.
          return;
        }

        consecutiveFailedAttemptCount += 1;
        // Make sure HEAD packet is sent and allow brief timeout errors.
        const failedToSendHeadPacket = packetNumber < 3;
        const exceededMaxConsecutiveFailedAttempts =
          consecutiveFailedAttemptCount > MAX_CONSECUTIVE_FAILED_ATTEMPTS;
        if (
          !isResponseOk ||
          failedToSendHeadPacket ||
          exceededMaxConsecutiveFailedAttempts
        ) {
          const capturedError = error as Error;
          const errorMessage = [
            `Message: ${capturedError.message}`,
            `failedToSendHeadPacket: ${failedToSendHeadPacket}`,
            `exceededMaxConsecutiveFailedAttempts: ${exceededMaxConsecutiveFailedAttempts}`,
            `isResponseOk: ${isResponseOk}`,
          ].join(', ');

          isClientInitiatedDisconnect = true;
          socket.disconnect();
          sendBack({
            type: 'connection.sendAudioPacket.failed',
            message: errorMessage,
          });
        } else {
          // Under threshold: keep recording, but this packet went un-ACKed so
          // the websocket file may be incomplete — taint it (see flag). [ENG-4721]
          hadUnackedAudioPacket = true;
          logger(
            'warn',
            'Audio packet not ACKed; websocket file may be incomplete',
            {
              packetNumber: currentPacketNumber,
              consecutiveFailedAttemptCount,
            },
          );
        }
      }
    };

    const socket = io(`${input.apiDomain}/live-transcription`, {
      query: { appointmentId },
      reconnection: false,
      reconnectionAttempts: 100,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      autoConnect: false,
    });

    socket.on(TRANSCRIPT_EVENT, (transcript: TranscriptEvent) => {
      // There's no point in doing a send back if we get an empty string
      if (transcript.transcript) {
        sendBack({
          type: 'connection.transcript.received',
          transcript,
        });
      }
    });

    socket.on(CONNECT, () => {
      initialize();
    });

    socket.on(CONNECT_ERROR, () => {
      sendBack({
        type: 'connection.failed',
        message: 'Received connect-error event',
      });
    });

    socket.on(DISCONNECT, () => {
      if (isClientInitiatedDisconnect) {
        sendBack({
          type: 'connection.disconnected',
        });
      } else {
        sendBack({
          type: 'connection.disconnected.unexpectedly',
          message: 'Connection was unexpectedly closed by the server.',
        });
      }
      audioPacketSubscription?.unsubscribe();
    });

    const initialize = async () => {
      try {
        const initPayload: InitializePacketHandler = {
          encoding: format.encoding,
          audioFormat: format.extension,
          segmentId: input.segmentId,
          recordingStartedAt: input.recordingStartedAt,
        };

        const response = await socket
          .timeout(10000)
          .emitWithAck(INITIALIZE_AUDIO_HANDLER, initPayload);

        if (!response.ok) {
          sendBack({
            type: 'connection.failed',
            message: 'Unable to initialize audio handler',
          });
          return;
        }

        // This we need to do since initializeAudioHandler is not apollo client operation
        // Information about the segment in cache can be used to determine if the segment is created successfully
        cacheUtils.writeFragment({
          fragment: appointmentSegmentFragment,
          key: { id: input.segmentId },
          data: {
            __typename: 'AppointmentSegments',
            id: input.segmentId,
            appointmentId: input.appointmentId,
            wasDegraded: false,
          },
        });

        // `concatMap`, not `switchMap`: send + ACK each packet in arrival order.
        // `switchMap` cancels the in-flight send when the next packet arrives
        // within the ACK RTT, dropping sends — most dangerously the encoder tail
        // at teardown. `concatMap` also drains cleanly: on subject completion it
        // finishes every queued send before completing. Mirrors audioFileSystem
        // [ENG-3703]. [ENG-4721]
        audioPacketSubscription = input.audioPacketSubject
          .asObservable()
          .pipe(concatMap((packet) => sendAudioPacket(packet)))
          .subscribe({
            complete: () => {
              if (teardownSafetyTimer) {
                clearTimeout(teardownSafetyTimer);
                teardownSafetyTimer = undefined;
              }
              // Only emit STOP_RECORDING as a clean finalize if every packet was
              // ACKed. If any went un-ACKed (possibly the tail), the file may be
              // truncated, so hard-close instead: the server finalizes what it
              // has, its hash won't match, and the backup is used. [ENG-4721]
              if (hadUnackedAudioPacket) {
                logger(
                  'warn',
                  'Websocket drain completed with un-ACKed packet(s); finalizing via backup',
                );
                hardClose();
                return;
              }
              finalizeServerRecording();
            },
          });

        sendBack({
          type: 'connection.connected',
        });
      } catch (error) {
        sendBack({
          type: 'connection.failed',
          message: 'Timeout while initializing audio handler',
        });
      }
    };

    receive((event) => {
      if (event.type === 'connection.connect') {
        socket.auth = { token: event.authToken, tenantId };
        if (!socket.connected) {
          socket.connect();
        }
      }
    });

    return () => {
      logger('info', 'Closing websocket connection', {
        numAudioPacketsConsumedByWebSocket:
          input.numAudioPacketsConsumedByWebSocket,
        totalBytesConsumedByWebSocket: input.totalBytesConsumedByWebSocket,
      });

      // Don't cancel the drain here: on a normal stop `concatMap` is still
      // flushing the queued tail and awaiting ACKs, and unsubscribing/
      // disconnecting now would truncate the file. The drain's `complete` handler
      // finalizes and the server closes the socket; the safety timer below is the
      // backstop if it never finishes (forced teardown / wedged ACK). [ENG-4721]
      if (hasStoppedRecording || !socket.connected) {
        // Already finalized (or nothing to drain) — close immediately.
        hardClose();
        return;
      }
      teardownSafetyTimer = setTimeout(hardClose, TEARDOWN_SAFETY_TIMEOUT_MS);
    };
  },
);
